import { JsonPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  OnDestroy,
  OnInit,
  output,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { FilesApiService } from '../../../../../../services/api/files.service';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { ModalHelperService } from '../../../../../../services/modal-helper.service';
import { PermissionFacadeService } from '../../../../../../services/permission-facade.service';
import { TestModeService } from '../../../../../../services/test-mode.service';
import { ToastService } from '../../../../../../services/toast.service';
import { CampaignStatus } from '../../../../../../types/campaign.interface';
import { ChoiceListElement } from '../../../../../../types/choice-list-element.interface';
import { InfoType } from '../../../../../../types/info.interface';
import { CesBottomActions } from '../../../../../ui/ces-bottom-actions/ces-bottom-actions.component';
import { CesButtonSecondaryComponent } from '../../../../../ui/ces-buttons/ces-button-secondary.component';
import { CesIconComponent } from '../../../../../ui/ces-icon/ces-icon.component';
import { CesProperty } from '../../../../../ui/ces-property/ces-property.component';
import { downloadBlob } from '../../../../../../utils/download.utils';
import { CesGridSkeletonComponent } from '../../../../../ui/skeletons/grid-skeleton.component';
import { openCampaignPlanningDialog } from '../planning/open-planning-dialog.util';
import { LoadingSpinnerComponent } from '../sub-components/ces-loading-spinner/loading-spinner.component';
import {
  AuscultationValue,
  CampaignAuscultationService,
  DisplayedFile,
  FileUploadError,
} from '../sub-services/campaign-auscultation.service';
import { CampaignPageStore } from '../sub-services/campaign-page.store';

// Status 0 means no answer reached the browser (network, proxy, timeout).
const UPLOAD_FAILURE_MESSAGES = new Map<number, string>([
  [0, 'common.toasts.fileUploadNoResponse'],
  [409, 'common.toasts.fileAlreadyAttached'],
  [413, 'common.toasts.fileTooLarge'],
]);

@Component({
  selector: 'ces-campaign-auscultation',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    JsonPipe,
    NgClass,
    FormsModule,
    TableModule,
    SelectModule,
    TextareaModule,
    CesIconComponent,
    CesBottomActions,
    LoadingSpinnerComponent,
    CesButtonSecondaryComponent,
    CesGridSkeletonComponent,
    CesProperty,
    TranslocoModule,
  ],
  providers: [DialogService],
  templateUrl: './campaign-auscultation.component.html',
  styleUrl: './campaign-auscultation.component.scss',
})
export class CesCampaignAuscultationComponent implements OnInit, OnDestroy {
  readonly hasChanges = output<boolean>();
  @ViewChild('dt') dt!: Table;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private pendingFileAuscultationValue: AuscultationValue | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly dialogService = inject(DialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly translate = inject(TranslocoService);
  readonly t = inject(TestModeService);
  readonly pageStore = inject(CampaignPageStore);
  readonly ausc = inject(CampaignAuscultationService);
  readonly fileService = inject(FilesApiService);
  private readonly permissionFacade = inject(PermissionFacadeService);
  private readonly toastService = inject(ToastService);
  readonly InfoType = InfoType;
  private readonly langSignal = signal(this.translate.getActiveLang());

  readonly hasWriteAccess = this.permissionFacade.hasWriteAccess('monitoring/campaigns/:id/auscultation');
  readonly hasAuscultationReadAccess = this.permissionFacade.hasReadAccess('configuration/measures');

  readonly isReadOnly = computed(() => {
    const campaign = this.pageStore.currentCampaign();
    const campaignDone = campaign?.status === CampaignStatus.DONE;
    return campaignDone || !this.hasWriteAccess();
  });

  readonly tableData = computed(() => {
    if (!this.pageStore.isAuscultationReady()) return [];
    return this.ausc.auscultationValues();
  });

  constructor() {
    effect(() => {
      const hasChanges = this.ausc.hasGlobalChanges();
      this.hasChanges.emit(hasChanges);
    });
  }

  ngOnInit(): void {
    this.translate
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.langSignal.set(this.translate.getActiveLang());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCurrentTextValue(auscultationValue: AuscultationValue): string {
    const currentValue = this.ausc.getCurrentValue(auscultationValue.campaignInfo.id);
    if (typeof currentValue === 'string') return currentValue;
    if (typeof currentValue === 'number') return String(currentValue);
    return typeof auscultationValue.value === 'string' ? auscultationValue.value : '';
  }

  getCurrentChoiceValue(auscultationValue: AuscultationValue): number | null {
    const currentValue = this.ausc.getCurrentValue(auscultationValue.campaignInfo.id);
    const value = currentValue ?? auscultationValue.value ?? null;
    return value ? Number(value) : null;
  }

  getCurrentChoiceDisplayValue(auscultationValue: AuscultationValue): string {
    const choiceId = this.getCurrentChoiceValue(auscultationValue);
    if (!choiceId) return '';

    const choiceElement = this.ausc.choiceListElementStore.entities().find((el: ChoiceListElement) => el.id === choiceId);
    return choiceElement?.name ?? '';
  }

  async updateTextValue(auscultationValue: AuscultationValue, event: Event): Promise<void> {
    const target = event.target as HTMLTextAreaElement;
    const newValue = target?.value || '';

    this.ausc.updatePendingValue(auscultationValue.campaignInfo.id, newValue);
  }

  async updateChoiceValue(auscultationValue: AuscultationValue, newChoiceId: number | null): Promise<void> {
    this.ausc.updatePendingValue(auscultationValue.campaignInfo.id, newChoiceId);
  }

  addFile(auscultationValue: AuscultationValue, file: File): void {
    if (auscultationValue.info.type === InfoType.IMAGE && !file.type.startsWith('image/')) {
      this.toastService.warning('common.toasts.imageOnlyAllowed');
      return;
    }
    this.ausc.addPendingFile(auscultationValue.campaignInfo.id, file);
  }

  removeFile(auscultationValue: AuscultationValue, fileId: string | number, isPending: boolean): void {
    this.ausc.removePendingFile(auscultationValue.campaignInfo.id, fileId, isPending);
  }

  async onFileClick(file: DisplayedFile): Promise<void> {
    if (file.isPending) return;
    try {
      const res = await this.fileService.getFileAsync(file.path, file.name);
      if (res.body) {
        downloadBlob(res.body, file.name);
      }
    } catch {
      this.toastService.error('common.toasts.downloadFailed');
    }
  }

  async onSave(): Promise<void> {
    try {
      await this.pageStore.saveAllAuscultationChangesWithHistory();
    } catch (error) {
      if (!(error instanceof FileUploadError)) {
        this.toastService.warning('common.toasts.saveFailed');
        return;
      }
      const key = UPLOAD_FAILURE_MESSAGES.get(error.status) ?? 'common.toasts.fileUploadFailed';
      this.toastService.warning(key, undefined, undefined, undefined, { name: error.fileName, status: error.status });
    }
  }

  async onCancel(): Promise<void> {
    if (
      this.ausc.hasGlobalChanges() &&
      !(await this.confirmationHelper.ask(
        'entities.campaign.messages.confirmDiscard',
        'entities.automaticPost.messages.editMode.unsavedTitle',
        undefined,
        'pi pi-exclamation-triangle',
        'common.buttons.discard',
      ))
    ) {
      return;
    }
    this.ausc.cancelAllChanges();
  }
  triggerFileSelection(auscultationValue: AuscultationValue): void {
    this.pendingFileAuscultationValue = auscultationValue;
    this.fileInput.nativeElement.accept = auscultationValue.info.type === InfoType.IMAGE ? 'image/*' : '';
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file && this.pendingFileAuscultationValue) {
      this.addFile(this.pendingFileAuscultationValue, file);
      input.value = '';
    }
    this.pendingFileAuscultationValue = null;
  }

  cancelFileRemoval(auscultationValue: AuscultationValue, fileId: string | number): void {
    this.ausc.cancelPendingFileRemoval(auscultationValue.campaignInfo.id, fileId);
  }

  readonly skeletonColumns = computed(() => {
    this.langSignal();
    return [
      this.translate.translate('common.labels.name'),
      this.translate.translate('common.labels.mandatory'),
      this.translate.translate('common.labels.locations'),
      this.translate.translate('common.labels.value'),
    ];
  });
  readonly skeletonWidths = ['30%', '10%', '25%', '35%'];

  isFieldModified(auscultationValue: AuscultationValue): boolean {
    const currentValue = this.ausc.getCurrentValue(auscultationValue.campaignInfo.id);
    const originalValue = auscultationValue.value;

    if (currentValue === null || currentValue === undefined) {
      return false;
    }

    return String(currentValue) !== String(originalValue ?? '');
  }

  openPlanningDialog(): void {
    const campaign = this.pageStore.currentCampaign();
    if (!campaign) return;

    openCampaignPlanningDialog(
      { dialogService: this.dialogService, modalHelper: this.modalHelper, translate: this.translate },
      { campaignId: campaign.id, assetId: campaign.assetId, mode: 'auscultations' },
      () => void this.pageStore.loadAuscultationData(true).catch(() => undefined),
    );
  }
}
